import type { AuthData } from "@loanlink-nl/zero";
import { mutators, schema } from "@loanlink-nl/zero";
import { getAuth, type ParsedToken } from "firebase/auth";
import { nextTick, ref } from "vue";
import { createZeroComposables } from "zero-vue";
import type { DefaultContext, DefaultSchema } from "@rocicorp/zero";

const lastClaims = ref<AuthData | undefined>();
const tokenRef = ref<string | undefined>();
let refreshPromise: Promise<boolean> | undefined;

export function setIdToken(token?: string, claims?: ParsedToken) {
	if (token === tokenRef.value && lastClaims.value?.sub === claims?.sub) {
		return;
	}

	tokenRef.value = token;
	lastClaims.value =
		claims?.sub !== undefined ? { sub: claims.sub } : undefined;
}

export async function refreshZeroAuth(zero = useZero().value) {
	if (refreshPromise) {
		return await refreshPromise;
	}

	refreshPromise = (async () => {
		const auth = getAuth();
		const user = auth.currentUser;

		if (!user) {
			setIdToken();
			return false;
		}

		const { token, claims } = await user.getIdTokenResult(true);
		setIdToken(token, claims);
		await nextTick();
		zero?.connection.connect({ auth: token });
		return true;
	})();

	try {
		return await refreshPromise;
	} finally {
		refreshPromise = undefined;
	}
}

export const { useZero, useQuery, useConnectionState } = createZeroComposables<
	DefaultSchema,
	undefined,
	DefaultContext
>(() => {
	return {
		schema,
		userID: lastClaims.value?.sub ?? "anon",
		server: import.meta.env.VITE_ZERO_CACHE_URL,
		mutators,
		auth: tokenRef.value,
		context: lastClaims.value,
	};
});
